import { useState, useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { Box, Typography } from "@mui/material";
import { handleClickSignIn, fetchAuth, logout } from "../lib/auth";
import { PoModalShell } from "./common/PoModalShell";
import { PoButton } from "./common/PoButton";
import { PoField, PoLabel, PoTextField } from "./common/PoForm";

interface SessionReloginModalProps {
  open: boolean;
  onSuccess: () => void;
}

export function SessionReloginModal({
  open,
  onSuccess,
}: SessionReloginModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      // Cognitoキャッシュセッションからメールアドレスを取得（失効後も token payload は残留）
      fetchAuthSession()
        .then((session) => {
          const e = session.tokens?.idToken?.payload?.email as string | undefined;
          if (e) setEmail(e);
        })
        .catch(() => {});
      // モーダル開く度にパスワードリセット
      setPassword("");
      setErrorMsg("");
    }
  }, [open]);

  const handleRelogin = async () => {
    if (!password) return;
    setLoading(true);
    setErrorMsg("");
    const result = await handleClickSignIn(email, password);
    if (result === "Login") {
      // トークンインターセプターを再初期化して作業継続
      await fetchAuth();
      setPassword("");
      onSuccess();
    } else {
      setErrorMsg("パスワードが正しくありません");
    }
    setLoading(false);
  };

  const handleGoToLogin = () => {
    logout();
  };

  return (
    // cmd-po_1199(T2a): raw Dialog → PoModalShell 移行（precedent=ProductEditModal:1163）
    // ★再ログインまで閉じない意図設計を保全（Modal onClose 未配線＝backdrop/Esc でも閉じない・cmd-po_1247 で✕は共通枠から全廃済＝旧クローズ抑止propは不要になり除去）
    <PoModalShell
      open={open}
      title="セッションの有効期限が切れました"
      onClose={() => {
        /* バックドロップ/Escキーで閉じない — セッション切れ時はログインかサインアウト必須 */
      }}
      testidPrefix="session-relogin"
      widthPx={440}
      footer={
        <>
          <PoButton
            variant="secondary"
            onClick={handleGoToLogin}
            data-testid="session-relogin-login-link"
            disabled={loading}
          >
            ログイン画面へ
          </PoButton>
          <PoButton
            variant="primary"
            onClick={handleRelogin}
            disabled={loading || !password}
            type="submit"
          >
            再ログイン
          </PoButton>
        </>
      }
    >
      <Typography
        sx={{
          fontSize: "var(--fs-12)",
          color: "var(--color-sumi-light)",
          lineHeight: 1.7,
        }}
      >
        入力中の内容は保持されています。パスワードを入力して再ログインすると、そのまま作業を続けられます。
      </Typography>

      {/* メールアドレス（表示のみ） */}
      <PoField fieldKey="session-relogin-email">
        <PoLabel>メールアドレス</PoLabel>
        <PoTextField
          fullWidth
          value={email}
          disabled
          inputProps={{ "aria-label": "メールアドレス" }}
        />
      </PoField>

      {/* パスワード */}
      <PoField fieldKey="session-relogin-password">
        <PoLabel required>パスワード</PoLabel>
        <PoTextField
          fullWidth
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && password) handleRelogin();
          }}
          error={!!errorMsg}
          helperText={errorMsg}
          inputProps={{ "aria-label": "パスワード" }}
        />
      </PoField>
    </PoModalShell>
  );
}
